import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useSpring, useTransform } from 'framer-motion';

interface LiveScoreboardProps {
    teamAScore: number;
    teamBScore: number;
    teamAAlignments: number;
    teamBAlignments: number;
    currentRound: number;
    maxRounds: number;
}

const AnimatedNumber: React.FC<{ value: number }> = ({ value }) => {
    const spring = useSpring(0, { stiffness: 100, damping: 30 });
    const display = useTransform(spring, (current) => Math.round(current));
    const [displayValue, setDisplayValue] = useState(0);

    useEffect(() => {
        spring.set(value);
    }, [spring, value]);

    useEffect(() => {
        const unsubscribe = display.onChange((latest) => setDisplayValue(latest));
        return unsubscribe;
    }, [display]);

    return <>{displayValue}</>;
};

const LiveScoreboard: React.FC<LiveScoreboardProps> = ({
    teamAScore,
    teamBScore,
    teamAAlignments,
    teamBAlignments,
    currentRound,
    maxRounds
}) => {
    const { t } = useTranslation();
    const totalScore = teamAScore + teamBScore || 1; // Avoid division by zero
    const teamAPercentage = (teamAScore / totalScore) * 100;
    const teamBPercentage = (teamBScore / totalScore) * 100;

    const scoreDiff = Math.abs(teamAScore - teamBScore);
    const leader = teamAScore > teamBScore ? 'A' : teamAScore < teamBScore ? 'B' : null;

    return (
        <div className="glass-card" style={{ padding: 'var(--panel-padding)' }}>
            <div style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                marginBottom: 'var(--space-lg)',
                color: 'var(--text-primary)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <span>SCOREBOARD</span>
                <span style={{
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                    fontWeight: '400'
                }}>
                    {t('round')} {currentRound}/{maxRounds}
                </span>
            </div>

            {/* Team A */}
            <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                style={{
                    marginBottom: 'var(--space-lg)',
                    padding: 'var(--space-md)',
                    background: 'rgba(59, 130, 246, 0.1)',
                    borderRadius: 'var(--radius-md)',
                    border: '2px solid var(--team-a)'
                }}
            >
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 'var(--space-sm)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                        <span style={{ fontSize: '1.5rem' }}>🔵</span>
                        <span style={{
                            fontSize: '1.125rem',
                            fontWeight: '700',
                            color: 'var(--team-a-light)'
                        }}>
                            {t('teamA')}
                        </span>
                        {leader === 'A' && (
                            <motion.span
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                style={{ fontSize: '1.25rem' }}
                            >
                                👑
                            </motion.span>
                        )}
                    </div>
                    <motion.div
                        className="text-gradient"
                        style={{
                            fontSize: '2rem',
                            fontWeight: '800'
                        }}
                        key={`score-a-${teamAScore}`}
                        initial={{ scale: 1.2, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: 'spring', stiffness: 500 }}
                    >
                        <AnimatedNumber value={teamAScore} /> {t('scores.points')}
                    </motion.div>
                </div>

                {/* Progress bar */}
                <div className="progress-bar" style={{ marginBottom: 'var(--space-sm)' }}>
                    <motion.div
                        className="progress-fill"
                        initial={{ width: 0 }}
                        animate={{ width: `${teamAPercentage}%` }}
                        transition={{ duration: 0.5, ease: 'easeOut' }}
                    />
                </div>

                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)'
                }}>
                    <span>
                        {t('scores.alignments')}: <strong style={{ color: 'var(--success)' }}>{teamAAlignments}</strong>
                        {teamAAlignments > 0 && (
                            <span style={{ marginLeft: '4px' }}>
                                (×{1 + teamAAlignments * 0.5})
                            </span>
                        )}
                    </span>
                    <span>{teamAPercentage.toFixed(0)}%</span>
                </div>
            </motion.div>

            {/* Team B */}
            <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                style={{
                    marginBottom: 'var(--space-lg)',
                    padding: 'var(--space-md)',
                    background: 'rgba(239, 68, 68, 0.1)',
                    borderRadius: 'var(--radius-md)',
                    border: '2px solid var(--team-b)'
                }}
            >
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 'var(--space-sm)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                        <span style={{ fontSize: '1.5rem' }}>🔴</span>
                        <span style={{
                            fontSize: '1.125rem',
                            fontWeight: '700',
                            color: 'var(--team-b-light)'
                        }}>
                            {t('teamB')}
                        </span>
                        {leader === 'B' && (
                            <motion.span
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                style={{ fontSize: '1.25rem' }}
                            >
                                👑
                            </motion.span>
                        )}
                    </div>
                    <motion.div
                        style={{
                            fontSize: '2rem',
                            fontWeight: '800',
                            background: 'linear-gradient(135deg, var(--team-b), #F87171)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text'
                        }}
                        key={`score-b-${teamBScore}`}
                        initial={{ scale: 1.2, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: 'spring', stiffness: 500 }}
                    >
                        <AnimatedNumber value={teamBScore} /> {t('scores.points')}
                    </motion.div>
                </div>

                {/* Progress bar */}
                <div className="progress-bar" style={{ marginBottom: 'var(--space-sm)' }}>
                    <motion.div
                        className="progress-fill"
                        style={{ background: 'linear-gradient(90deg, var(--team-b), #F87171)' }}
                        initial={{ width: 0 }}
                        animate={{ width: `${teamBPercentage}%` }}
                        transition={{ duration: 0.5, ease: 'easeOut' }}
                    />
                </div>

                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)'
                }}>
                    <span>
                        {t('scores.alignments')}: <strong style={{ color: 'var(--success)' }}>{teamBAlignments}</strong>
                        {teamBAlignments > 0 && (
                            <span style={{ marginLeft: '4px' }}>
                                (×{1 + teamBAlignments * 0.5})
                            </span>
                        )}
                    </span>
                    <span>{teamBPercentage.toFixed(0)}%</span>
                </div>
            </motion.div>

            {/* Score Difference */}
            {leader && scoreDiff > 0 && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    style={{
                        padding: 'var(--space-md)',
                        background: 'var(--bg-card)',
                        borderRadius: 'var(--radius-md)',
                        textAlign: 'center',
                        border: `1px solid ${leader === 'A' ? 'var(--team-a)' : 'var(--team-b)'}`
                    }}
                >
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                        {t('scores.gap')}
                    </div>
                    <div style={{
                        fontSize: '1.5rem',
                        fontWeight: '700',
                        color: leader === 'A' ? 'var(--team-a)' : 'var(--team-b)'
                    }}>
                        {scoreDiff} {t('scores.points')}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                        {leader === 'A' ? `🔵 ${t('teamA')}` : `🔴 ${t('teamB')}`} {t('scores.lead')}
                    </div>
                </motion.div>
            )}

            {/* Tie */}
            {!leader && currentRound > 0 && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    style={{
                        padding: 'var(--space-md)',
                        background: 'var(--bg-card)',
                        borderRadius: 'var(--radius-md)',
                        textAlign: 'center',
                        border: '1px solid var(--warning)'
                    }}
                >
                    <div style={{ fontSize: '1.25rem', marginBottom: '4px' }}>🤝</div>
                    <div style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--warning)' }}>
                        {t('scores.tie')}
                    </div>
                </motion.div>
            )}
        </div>
    );
};

export default LiveScoreboard;
